import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/core/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Calendar } from '@/shared/components/ui/Calendar';
import { useDateFormat } from '@/shared/hooks/useDateFormat';
import { parseDatePickerValue } from '@/shared/lib/datetime/instant';

export interface InlineDatePickerProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Date picker compacto para uso inline sin react-hook-form ni label flotante.
 *
 * Muestra el valor formateado según el formato de fecha del usuario y delega
 * la selección al Calendar subyacente.
 *
 * @example
 * <InlineDatePicker
 *   value="2024-01-15"
 *   onChange={(v) => setDate(v)}
 *   className="h-7 flex-1"
 * />
 */
export function InlineDatePicker({
  value,
  onChange,
  disabled = false,
  className,
  'data-testid': testId,
}: InlineDatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const { dateFormat } = useDateFormat();

  const selectedDate = value ? parseDatePickerValue(value) : undefined;

  const handleSelect = (date: Date | undefined) => {
    onChange(date ? date.toISOString().split('T')[0] : '');
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'flex items-center gap-1.5 rounded border border-input bg-transparent px-2 text-xs',
            'focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring',
            className,
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="flex-1 text-left">
            {value ? format(parseDatePickerValue(value), dateFormat) : '—'}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar selected={selectedDate} onSelect={handleSelect} mode="single" />
      </PopoverContent>
    </Popover>
  );
}
